import React from 'react';
import {useNavigate} from 'react-router';
import {Link, useSearchParams} from 'react-router-dom';
import {
    CheckCircleOutlined,
    ClockCircleOutlined, CloseCircleOutlined,
    PlusOutlined
} from '@ant-design/icons';
import { useQuery} from '@apollo/client/react';
import {Button, DatePicker, Input, Space, Table, Tooltip} from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';

import {AdminOrdersQuery, Enum_Order_Iikostatus, Enum_Order_Paymentstatus} from '../../../__generated__/graphql';
import {toAdminBalance, toAdminUser} from '../../../config/routes';
import {QUERY_ADMIN_ORDERS} from '../../../shared/graphql/admin/orders';
import {currencyFormat} from '../../../shared/helpers/currencyFormat';
import {phonePlainToInput} from '../../../shared/helpers/phoneFormat';

import {filtersToQuery, filtersToSearchParams, searchParamsToFilters} from './filter';

import cls from './Orders.module.scss'

type IOrderClient = NonNullable<NonNullable<AdminOrdersQuery['orders'][number]>['client']>

function paymentStatusDisplay (status:Enum_Order_Paymentstatus) {
    switch (status) {
        case 'CREATED_1': return <Tooltip title={'Платеж создан'}><ClockCircleOutlined className={cls.warning}/></Tooltip>
        case 'PAID_2': return <Tooltip title={'Платеж оплачен'}><CheckCircleOutlined className={cls.success}/></Tooltip>
        case 'ERROR_3': return <Tooltip title={'Ошибка платежа'}><CloseCircleOutlined className={cls.error}/></Tooltip>
    }
}

function iikoStatusDisplay (status:Enum_Order_Iikostatus) {
    switch (status) {
        case 'AWAIT_0': return <Tooltip title={'Ожидает поступления оплаты'}>—</Tooltip>
        case 'CREATED_1': return <Tooltip title={'Ожидает пополнения'}><ClockCircleOutlined className={cls.warning}/></Tooltip>
        case 'PROCESSED_2': return <Tooltip title={'В обработке'}><ClockCircleOutlined className={cls.warning}/></Tooltip>
        case 'FILLED_3': return <Tooltip title={'Кошелек пополнен'}><CheckCircleOutlined className={cls.success}/></Tooltip>
        case 'ERROR_4': return <Tooltip title={'Ошибка пополнения кошелька'}><CloseCircleOutlined className={cls.error}/></Tooltip>
    }
}

export const Orders = () => {

    const [searchParams, setSearchParams] = useSearchParams();
    const filters = searchParamsToFilters(searchParams);
    const navigate = useNavigate();

    const query = useQuery(QUERY_ADMIN_ORDERS, {
        variables: {
            filters: filtersToQuery(filters),
            pagination: {limit: 1000},
            sort: ['createdAt:DESC'],
        },
        fetchPolicy: 'no-cache',
    })

    const onSearchChange = (data: any) => {
        setSearchParams(filtersToSearchParams({
            ...filters,
            search: data,
        }))
    }

    const dataSource = _.map(query.data?.orders, item => ({
        ...item,
        key: item?.documentId!,
        createdAt: dayjs(item?.createdAt).format('DD.MM.YYYY HH:mm')
    }))

    const columns = [
        {
            title: 'Дата',
            dataIndex: 'createdAt',
            key: 'createdAt',
        },
        {
            title: 'UID',
            dataIndex: 'UID',
            key: 'UID',
        },
        {
            title: 'Ученик',
            dataIndex: 'client',
            key: 'client',
            render: (value:IOrderClient) => <Link to={toAdminBalance(value.documentId)}>{value.firstName} {value.lastName}</Link>
        },
        {
            title: 'Телефон',
            dataIndex: 'phone',
            key: 'phone',
            render: (value:string) => phonePlainToInput(value)
        },
        {
            title: 'Сумма',
            dataIndex: 'amount',
            key: 'amount',
            render: (value:number) => currencyFormat(value)
        },
        {
            title: 'Статус оплаты',
            dataIndex: 'paymentStatus',
            key: 'paymentStatus',
            className: cls.align,
            render: (status:Enum_Order_Paymentstatus) => paymentStatusDisplay(status),
        },
        {
            title: 'Статус пополнения',
            dataIndex: 'iikoStatus',
            key: 'iikoStatus',
            className: cls.align,
            render: (status:Enum_Order_Iikostatus) => iikoStatusDisplay(status),
        },
    ];

    const onPeriodChange = (data: any) => {
        setSearchParams(filtersToSearchParams({
            ...filters,
            period: data,
        }))
    }

    return (
        <div className={'admin-page'}>
            <div className={cls.filters}>
                <div className={cls.left}>
                    <Space>
                        <DatePicker.RangePicker onChange={onPeriodChange} allowClear={false}
                                                value={[filters.period[0], filters.period[1]]} format={'DD MMM YYYY'}/>
                        <Input.Search placeholder={'Поиск...'} onSearch={onSearchChange} allowClear={true}/>
                    </Space>
                </div>
                <div className={cls.right}>
                    <Button type={'primary'} icon={<PlusOutlined/>}
                            onClick={() => navigate(toAdminUser('new'))}>Добавить</Button>
                </div>
            </div>
            <Table dataSource={dataSource} columns={columns} loading={query.loading}/>

        </div>
    )

}